import React, { useState, useEffect, Fragment } from 'react'
import { connect } from 'react-redux'

import { Sidebar } from '@modul-connect/shared/components/organisms/side-bar/multi-side-bar'
import SideBarLinks from '@modul-connect/shared/components/organisms/side-bar-link'
import { SidebarLinkForSubpages } from '@modul-connect/shared/components/organisms/side-bar-link/side-bar-link'

import MainContainer from '@modul-connect/shared/components/organisms/main-container'
import { useParams, useNavigate } from 'react-router-dom'
import ModalAjaxError from '@modul-connect/shared/components/organisms/modal-ajax-error'
import BreadCrumbs from '@modul-connect/shared/components/molecules/breadcrumbs'
import { dismissAjaxError } from '../state/actions/app'
import { showFilterBar } from '../utils/app'

const MainPage = ({
  themes,
  saving,
  disturbanceBannerHeight = 0,
  rootURL,
  landingPageURL,
  initialPageIndex,
  hideBreadcrumbs = true,
  breadCrumbWordReplacements,
  sidebarContent,
  blockScrolling,
  dismissAjaxError,
}) => {
  const navigate = useNavigate()
  const { option } = useParams()

  const [selected, setSelected] = useState(initialPageIndex)
  const menuOpen = selected !== null && sidebarContent?.[selected]

  useEffect(() => {
    if (!sidebarContent?.length) return

    if (!option) {
      onSetSelected(initialPageIndex)
      return
    }

    const [category, title] = option.split('_')
    if (category !== rootURL) {
      onSetSelected(null)
      return
    }

    const idx = sidebarContent.findIndex((c) =>
      title?.toLowerCase().includes(c.id?.toLowerCase())
    )

    if (idx < 0) {
      onSetSelected(initialPageIndex)
      return
    }

    if (selected !== idx) setSelected(idx)
  }, [option, selected, sidebarContent, rootURL, initialPageIndex])

  const onSetSelected = (index) => {
    let url = '/' + rootURL
    const page = sidebarContent[index ?? 0]
    const isOnlyMainSubPage = page?.subPages?.length === 1 && page.subPages[0]?.id === 'main'
    const defaultSubPage = page?.subPages && !isOnlyMainSubPage
      ? page.subPages[0]?.id
      : undefined


    if (defaultSubPage) url += `/${rootURL}_${page.id}/${defaultSubPage}`
    else url += `/${rootURL}_${page.id}`
    navigate(url)
  }

  const onPanelClick = (subId) => {
    const page = sidebarContent[selected ?? 0]
    if (!page) return
    let url = '/' + rootURL + `/${rootURL}_${page.id}`
    if (subId) url += `/${subId}`
    navigate(url)
  }

  const renderPanel = (page) => {
    if (!page) return null
    const variant = 'new'

    const Header = (
      <Sidebar.Header variant={variant} icon={page.icon} title={page.title} />
    )

    if (Array.isArray(page.nodes) && page.nodes.length) {
      return (
        <>
          {Header}
          <Sidebar.Container>
            {page.nodes.map((node, i) => {
              switch (node.type) {
                case 'divider':
                  return <Sidebar.Divider key={`div-${i}`} variant={variant} />
                case 'spacing':
                  return <Sidebar.Spacing key={`div-${i}`} variant={variant} />
                case 'subtitle':
                  return (
                    <div 
                      style={{ marginLeft: node.indent ? '8px' : '0px' }} 
                    >
                      <Sidebar.Subtitle key={`sub-${i}`} variant={variant}>
                        {node.text}
                      </Sidebar.Subtitle>
                    </div>
                  )
                case 'item':
                  return (
                    <div 
                      key={node.id || i} 
                      style={{ marginLeft: node.indent ? '12px' : '0px' }} 
                    >
                      <Sidebar.Item id={node.id} variant={variant}>
                        <SidebarLinkForSubpages
                          id={node.id}
                          title={node.title}
                          subPages={page.subPages || []}
                          nodes={page.nodes.filter(x => x.type === 'item') || []}
                          onClick={onPanelClick}
                        />
                      </Sidebar.Item>
                    </div>
                  )
                case 'action':
                  return (
                    <Sidebar.ActionLink
                      key={`act-${i}`}
                      onClick={node.onClick || (() => {})}
                      variant={variant}
                    >
                      {node.text}
                    </Sidebar.ActionLink>
                  )
                default:
                  return null
              }
            })}
          </Sidebar.Container>
        </>
      )
    }

    const subPages = page.subPages || []
    return (
      <>
        {Header}
        {subPages.map((sp) => (
          <Sidebar.Item key={sp.id} id={sp.id} variant={variant}>
            <SidebarLinkForSubpages
              id={sp.id}
              title={sp.title}
              subPages={subPages}
              onClick={onPanelClick}
            />
          </Sidebar.Item>
        ))}
      </>
    )
  }

  const selectedPage = menuOpen ? sidebarContent[selected] : null

  return (
    <Fragment>
      <ModalAjaxError
        openModal={saving && saving.ajaxError}
        onClose={dismissAjaxError}
      />

      <MainContainer
        themes={themes}
        extend={[styles.main]}
        showFilterBar={showFilterBar}
        disturbanceBannerHeight={disturbanceBannerHeight}
        blockScrolling={blockScrolling}
      >
        {!hideBreadcrumbs && (
          <BreadCrumbs
            keepQueryString
            rootUrl={landingPageURL}
            wordReplacements={breadCrumbWordReplacements}
          />
        )}
        {menuOpen && selectedPage?.content}
      </MainContainer>

      <Sidebar themes={themes} variant="new" open={!!menuOpen} showFilterBar={showFilterBar} disturbanceBannerHeight={disturbanceBannerHeight}>
        <Sidebar.Rail>
          <SideBarLinks
            iconOnly
            isSelected={(index) =>
              (index || index === 0) && (selected || selected === 0)
                ? index === selected
                : false
            }
            sidebarContent={sidebarContent}
            onClick={onSetSelected}
          />
        </Sidebar.Rail>

        <Sidebar.Panel>{renderPanel(selectedPage)}</Sidebar.Panel>
      </Sidebar>
    </Fragment>
  )
}

const styles = {
  main: ({ theme: { layout } }) => ({
    padding: '0 16px',
    left: `${layout.sidebarWidth}px`,
    right: 0,
    bottom: 0,
  }),
}

const mapStateToProps = ({ themes, saving, disturbanceBannerHeight }) => ({
  themes,
  saving,
  disturbanceBannerHeight: disturbanceBannerHeight ?? 0,
})

const mapDispatchToProps = (dispatch) => ({
  dismissAjaxError: () => dispatch(dismissAjaxError()),
})

export default connect(mapStateToProps, mapDispatchToProps)(MainPage)